import * as React from "react";
import { Ban, KeyRound, MailCheck, Pencil, Send, UserCheck, UserPlus, Users } from "lucide-react";
import {
  ROLES,
  ROLE_LABELS,
  ROLE_REACH_LABELS,
  canAssignRole,
  hasSiteMemberships,
  isPlatformRole,
  reachIsMembershipBased,
  type Company,
  type Pagination as PageMeta,
  type Role,
  type Site,
  type UserWithContext,
} from "@taskiq/shared";
import { PersonAvatar } from "@/components/ui/Avatar";
import { PhoneField } from "@/components/ui/PhoneField";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useConfirm, useDiscardGuard } from "@/components/ui/confirm-context";
import { EmptyState, ErrorState, FormMessage } from "@/components/ui/Feedback";
import { Field, Form, FormSection, Input } from "@/components/ui/Field";
import { FilterBar } from "@/components/ui/FilterBar";
import { FilterSelect } from "@/components/ui/FilterSelect";
import { IconButton } from "@/components/ui/IconButton";
import { Identity } from "@/components/ui/Identity";
import { Modal } from "@/components/ui/Modal";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { Pagination } from "@/components/ui/Pagination";
import { PageHeader } from "@/components/ui/PageHeader";
import { SearchInput } from "@/components/ui/SearchInput";
import { useToast } from "@/components/ui/toast-context";
import { SkeletonRows, Table, Td, TdSpan, Th, Tr } from "@/components/ui/Table";
import { TimezoneSelect, browserTimezone } from "@/components/ui/TimezoneSelect";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PLACEHOLDER } from "@/lib/placeholders";
import { useTenancy } from "@/lib/tenancy";
import { LIST_PAGE_SIZE } from "@/lib/paging";
import { SEARCH_DEBOUNCE_MS, useDebounced } from "@/lib/useDebounced";
import { useApi } from "@/lib/useApi";

interface UserListResponse {
  users: UserWithContext[];
  pagination: PageMeta;
}

type SiteRow = Pick<Site, "id" | "name" | "timezone" | "company_id">;

interface SiteListResponse {
  sites: (SiteRow & { company: { id: string; name: string; slug: string } | null })[];
}

interface CompanyListResponse {
  companies: Company[];
}

/**
 * Roles as picker options, with what each one actually reaches on the second
 * line.
 *
 * `ROLE_REACH_LABELS` rather than a sentence typed here: "Every site in their
 * company" is a fact about the role that `rbac.ts` owns, and an admin choosing
 * between two roles is choosing between those two sentences. It used to live in
 * a `hint` under the whole field, which could only describe the rule for picking
 * a role and never the roles themselves.
 */
function roleOptions(roles: readonly Role[]) {
  return roles.map((role) => ({
    value: role,
    label: ROLE_LABELS[role],
    hint: ROLE_REACH_LABELS[role],
  }));
}

/**
 * `emailed` is false when the address already had an identity: Supabase refuses
 * to invite one twice, so the controller adopts it and no mail goes out. The
 * account is real either way, which is why this is a flag on a 201 rather than
 * an error.
 */
interface InviteResponse {
  user: UserWithContext;
  emailed: boolean;
}

const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
  { value: "disabled", label: "Deactivated" },
];

const ROLE_OPTIONS = [
  { value: "", label: "Any role" },
  ...ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] })),
];

/**
 * Team - who works at this site, and what they may do.
 *
 * **The site is in the URL, not in a filter.** `/sites/12/users` is what this
 * page reads, so the list belongs to the site in the switcher and says so in the
 * network tab. An administrator covering three yards reads them one at a time,
 * which is the same trade every screen in the console makes.
 *
 * Every difference between what a Visionify staffer sees here and what a
 * customer's admin sees comes from `useTenancy()`, never from a role comparison.
 * The one place a role name is read is to *label* a person, which is a fact about
 * them rather than a decision about the reader.
 */
export function Team() {
  const { can } = useAuth();
  const { activeSiteId } = useTenancy();

  const [status, setStatus] = React.useState("");
  const [role, setRole] = React.useState("");
  const [search, setSearch] = React.useState("");
  // Debounced, because the SWR key is the URL: without this every keystroke is
  // a request, and the responses can land out of order so the list settles on
  // whichever query the server happened to finish last.
  const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = React.useState(1);

  // Narrowing moves the last page underneath you, and so does switching site -
  // page 3 of a two-page roster is an empty table that reads as "nobody here".
  React.useEffect(() => {
    setPage(1);
  }, [status, role, debouncedSearch, activeSiteId]);

  const query = new URLSearchParams({ page: String(page), pageSize: String(LIST_PAGE_SIZE) });
  if (status) query.set("status", status);
  if (role) query.set("role", role);
  const trimmed = debouncedSearch.trim();
  if (trimmed) query.set("search", trimmed);

  const { data, error, isLoading, mutate } = useApi<UserListResponse>(
    activeSiteId ? `/sites/${activeSiteId}/users?${query}` : null
  );

  const [inviting, setInviting] = React.useState(false);
  const [editing, setEditing] = React.useState<UserWithContext | null>(null);

  const canInvite = can("user.invite");
  const canEdit = can("user.write");

  const activeFilters = [status, role, trimmed].filter(Boolean).length;
  const filtered = activeFilters > 0;

  return (
    <div className="page">
      <PageHeader
        title="Team"
        description="Who's assigned to this location, and everywhere else they can reach."
        actions={
          canInvite && (
            <Button icon={<UserPlus />} onClick={() => setInviting(true)}>
              Invite
            </Button>
          )
        }
      />

      <FilterBar
        activeCount={activeFilters}
        onClear={() => {
          setStatus("");
          setRole("");
          setSearch("");
        }}
        search={
          <SearchInput
            label="Search the team by name or email"
            placeholder="Search name or email"
            value={search}
            onChange={setSearch}
          />
        }
      >
        {/* Role before status, the same order the phone's filter sheet uses.
            They were the other way round here and nowhere else. */}
        <FilterSelect
          label="Filter by role"
          value={role}
          active={role !== ""}
          options={ROLE_OPTIONS}
          onChange={setRole}
        />
        <FilterSelect
          label="Filter by status"
          value={status}
          active={status !== ""}
          options={STATUS_OPTIONS}
          onChange={setStatus}
        />
      </FilterBar>

      {error && <ErrorState error={error} onRetry={() => void mutate()} />}

      {/* Suppressed while a failure is on screen with nothing cached behind it.
          An empty table under an error banner is a header strip with no body -
          it reads as "there is nobody on your team", which is a different and
          much more alarming claim than "we could not load your team". */}
      {!(error && !data) && (
        <>
          <Table
            head={
              <>
                <Th>Person</Th>
                <Th hideBelow="md">Sites</Th>
                <Th hideBelow="sm">Role</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </>
            }
          >
            {isLoading && (
              <SkeletonRows
                columns={[
                  { kind: "identity", round: true },
                  { kind: "text", hideBelow: "md" },
                  { kind: "badge", hideBelow: "sm" },
                  { kind: "badge", width: "narrow" },
                  { kind: "blank" },
                ]}
              />
            )}

            {data && data.users.length === 0 && (
              <tr>
                <TdSpan colSpan={5}>
                  <EmptyState
                    icon={<Users />}
                    title={filtered ? "Nobody matches that" : "Nobody here yet"}
                    description={
                      filtered
                        ? "Try a different status or role, or clear the search."
                        : "Invite technicians and supervisors so they can run assessments."
                    }
                    action={
                      !filtered &&
                      canInvite && (
                        <Button onClick={() => setInviting(true)}>Invite your first</Button>
                      )
                    }
                  />
                </TdSpan>
              </tr>
            )}

            {data?.users.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                onEdit={canEdit ? () => setEditing(member) : undefined}
                onChanged={() => void mutate()}
              />
            ))}
          </Table>

          {data && (
            <Pagination pagination={data.pagination} onPageChange={setPage} label="people" />
          )}
        </>
      )}

      {canInvite && (
        <InviteDialog
          open={inviting}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            void mutate();
          }}
        />
      )}

      {canEdit && editing && (
        <MemberDialog
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void mutate();
          }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────── one row ──────────────────────────────── */

function MemberRow({
  member,
  onEdit,
  onChanged,
}: {
  member: UserWithContext;
  onEdit?: () => void;
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);
  const [failure, setFailure] = React.useState<string | null>(null);

  // Deactivating yourself is refused by the API and would lock you out if it
  // were not. Hidden rather than disabled: an action nobody may ever take is
  // not a permission being withheld.
  const isSelf = member.id === user?.id;
  const isInvited = member.status === "invited";
  const isDisabled = member.status === "disabled";
  const name = member.full_name ?? member.email;

  /**
   * Deactivation and reactivation are two different requests on purpose.
   * `DELETE` is the revoke verb - the same one that cancels an unaccepted
   * invitation, because those were never two different acts - and coming back is
   * a status change rather than a fresh invitation, so the person keeps their
   * identity, their memberships and the `invited_by` trail.
   *
   * **Both halves ask.** Only the revoking one used to, on the argument that
   * reactivating is reversible in one click and a confirmation on something
   * harmless teaches people to click through the one that matters. That argument
   * undercounts what the click does: it restores somebody's ability to sign in
   * and to file assessments, and the two buttons sit side by side in the same
   * cell of the same row. Granting access back to the wrong person from a
   * misclick is not the kind of reversible that helps, because nobody knows to
   * reverse it.
   *
   * The two prompts are deliberately not symmetrical. Revoking is `danger` and
   * red; restoring is the ordinary confirm, and its message says what the person
   * gets back rather than warning about it.
   */
  async function setActive(active: boolean) {
    const ok = active
      ? await confirm({
          title: `Reactivate ${name}?`,
          message:
            "They can sign in again straight away, with the locations and role they had before.",
          confirmLabel: "Reactivate",
          cancelLabel: "Leave deactivated",
        })
      : await confirm({
          title: isInvited ? "Cancel this invitation?" : `Deactivate ${name}?`,
          message: isInvited
            ? "They will not be able to use the link in their invitation email. You can invite them again later."
            : "They lose access immediately. Their assessments stay on record, and you can reactivate them at any time.",
          confirmLabel: isInvited ? "Cancel invitation" : "Deactivate",
          cancelLabel: "Keep access",
          danger: true,
        });

    if (!ok) return;

    setBusy(true);
    setFailure(null);
    try {
      if (active) await api(`/users/${member.id}`, { method: "PATCH", body: { status: "active" } });
      else await api(`/users/${member.id}`, { method: "DELETE" });

      // The row this button lives on is about to be replaced by the refetch, so
      // the confirmation has to outlive it. Failures stay inline below, where
      // they sit next to the control that produced them.
      if (active) toast.success("Can sign in again", { description: name });
      else if (isInvited) toast.success("Invitation cancelled", { description: name });
      else {
        toast.success("Deactivated", {
          description: `${name}. Their assessments stay on record.`,
        });
      }

      onChanged();
    } catch (err) {
      setFailure(apiErrorMessage(err, "Could not update that account"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Tr>
      <Td>
        <Identity media={<PersonAvatar person={member} />} title={name} subtitle={member.email} />
        {failure && (
          <p role="alert" className="row-error">
            {failure}
          </p>
        )}
      </Td>

      <Td hideBelow="md">
        <SiteReach member={member} />
      </Td>

      <Td hideBelow="sm">
        {/* Visionify staff stand out from the customer's own people. Purely
            presentational - `isPlatformRole` says so, and nothing about access
            is decided from it. Superadmin gets its own accent tone for the
            same reason; admin and field officer share neutral because neither
            outranks the other in what this badge is showing. */}
        <Badge tone={isPlatformRole(member.role) ? "accent" : "neutral"}>
          {ROLE_LABELS[member.role]}
        </Badge>
      </Td>

      <Td>
        {/* Both are real, expected states rather than anomalies, so they are
            labelled rather than hidden. `invited` in particular is easy to miss:
            the profile is created when the invite email goes out, so somebody who
            never opened it looks exactly like a colleague without this badge. */}
        {isInvited && <Badge tone="caution">Invited</Badge>}
        {isDisabled && <Badge tone="stop">Deactivated</Badge>}
        {!isInvited && !isDisabled && <Badge tone="ok">Active</Badge>}
      </Td>

      {/* Two visible buttons rather than a menu behind an ellipsis. Editing
          somebody and revoking their access are the whole of what this page
          does, and hiding both behind a click makes the common one a
          two-step. */}
      <Td align="right">
        <div className="row-actions">
          {onEdit && (
            <IconButton size="sm" label={`Edit ${name}`} onClick={onEdit}>
              <Pencil />
            </IconButton>
          )}
          {onEdit && !isSelf && !isDisabled && (
            <IconButton
              size="sm"
              tone="danger"
              disabled={busy}
              label={isInvited ? `Cancel the invitation for ${name}` : `Deactivate ${name}`}
              onClick={() => void setActive(false)}
            >
              <Ban />
            </IconButton>
          )}
          {onEdit && !isSelf && isDisabled && (
            <IconButton
              size="sm"
              disabled={busy}
              label={`Reactivate ${name}`}
              onClick={() => void setActive(true)}
            >
              <UserCheck />
            </IconButton>
          )}
        </div>
      </Td>
    </Tr>
  );
}

/**
 * Where a person works.
 *
 * Only a field officer's reach is a finite, named list, and it is the one case
 * where `member.sites` **is** the authorization - embedded on every row already,
 * default first, so every pill here is a real site name rather than a count
 * behind "+2 more".
 *
 * Admin and superadmin both reach a whole tier rather than a set of rows, and
 * neither gets pills for it. An admin's `site_memberships` rows are bookkeeping
 * a demotion needs, not a grant - usually just the one default site - so
 * treating them as "the sites" would under-report a reach that is genuinely
 * every site in the company. Naming the real list would mean fetching every
 * company's sites just to throw away all but one row's worth on most page
 * loads, for a fact that is stated more honestly as a single badge anyway.
 * Superadmin is the same shape one tier up: reach spans every company, so
 * there is no finite list to name at all.
 */
function SiteReach({ member }: { member: UserWithContext }) {
  if (!hasSiteMemberships(member.role)) {
    return <Badge tone="accent">All workspaces & locations</Badge>;
  }

  if (!reachIsMembershipBased(member.role)) {
    return <Badge tone="accent">All locations</Badge>;
  }

  const sites = member.sites ?? [];

  if (sites.length === 0) {
    return <Badge tone="caution">No locations assigned</Badge>;
  }

  return (
    <div className="site-pills">
      {sites.map((site) => (
        <Badge key={site.id} tone={site.id === member.site_id ? "accent" : "neutral"}>
          {site.name}
        </Badge>
      ))}
    </div>
  );
}

/* ─────────────────────────────── edit ─────────────────────────────── */

interface MemberForm {
  full_name: string;
  role: Role;
  site_id: string;
  phone: string;
  timezone: string;
}

/**
 * Everything about one person.
 *
 * A modal at `dialog--wide`, in two columns. It was a drawer on the argument
 * that the row behind it stays visible - which is true and turned out not to be
 * worth much, because nothing on the row is being compared against the form.
 * What the drawer did cost was shape: five short fields stacked down a 28rem
 * column is a lot of scrolling for a phone number, and the site list at the
 * bottom of it was below the fold on a laptop.
 *
 * The drawer comes back when this grows a crew history or an escalation chain.
 * Until then two columns fit it on one screen with no scroll region at all.
 *
 * **The PATCH carries only what changed.** Sending the whole form would rewrite
 * fields the editor never touched, which matters here more than usual: two
 * administrators looking at the same person would each overwrite the other's
 * edit with stale values they never saw.
 */
function MemberDialog({
  member,
  onClose,
  onSaved,
}: {
  member: UserWithContext;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();

  const initial = React.useMemo<MemberForm>(
    () => ({
      full_name: member.full_name ?? "",
      role: member.role,
      site_id: member.site_id,
      phone: member.phone ?? "",
      timezone: member.timezone,
    }),
    [member]
  );

  const [form, setForm] = React.useState<MemberForm>(initial);
  const [siteIds, setSiteIds] = React.useState<string[]>(() =>
    (member.sites ?? []).map((site) => site.id)
  );
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [resent, setResent] = React.useState(false);
  const [resetSent, setResetSent] = React.useState(false);

  // `company_id` goes to the API rather than into a `.filter()` here. Asking for
  // `/sites?pageSize=100` and sifting the result meant a superadmin pulled the
  // first hundred sites on the *platform* and kept whichever happened to belong
  // to this member's company - which for a tenant sorted past that hundred is
  // none of them, and the picker renders empty for a company that has sites.
  const { data: companySites } = useApi<SiteListResponse>(
    `/sites?pageSize=100&company_id=${member.company_id}`
  );
  const sitesInCompany = React.useMemo(() => companySites?.sites ?? [], [companySites]);

  const initialSiteIds = React.useMemo(
    () => (member.sites ?? []).map((site) => site.id),
    [member.sites]
  );

  // Both pickers speak strings, because an option's value is also its React key
  // and half of what the search matches. Site ids are converted at this boundary
  // rather than inside the components, so nothing downstream has to wonder which
  // of the two a given `site_id` is.
  const siteOptions = React.useMemo(
    () =>
      sitesInCompany.map((site) => ({
        value: String(site.id),
        label: site.name,
        hint: site.timezone,
      })),
    [sitesInCompany]
  );

  // The same list, plus the one rule the assignment picker has that the default
  // picker does not: the default site is always in, and the database will not
  // let it out (`app.protect_home_site_membership`).
  const assignableSiteOptions = React.useMemo(
    () =>
      siteOptions.map((option) =>
        option.value === String(member.site_id)
          ? {
              ...option,
              locked: true,
              lockReason: "Their default location. Change the default above to move it.",
            }
          : option
      ),
    [siteOptions, member.site_id]
  );

  const patch = diff(initial, form);
  const sitesChanged = !sameIds(initialSiteIds, siteIds);
  const dirty = Object.keys(patch).length > 0 || sitesChanged;

  const requestClose = useDiscardGuard(dirty, saving, onClose);
  const confirm = useConfirm();

  const isSelf = member.id === user?.id;
  const assignableRoles = ROLES.filter((candidate) => user && canAssignRole(user.role, candidate));

  // Same rank rule as assigning a role: an admin may reset anyone at or below
  // their own rank, a superadmin may reset anyone. Excluded for yourself - you
  // already have a session and know your current password, so "Change
  // password" on your own account page is the right door, not this one.
  const canResetPassword =
    !isSelf && member.status === "active" && user !== null && canAssignRole(user.role, member.role);

  /**
   * Only a field officer gets a site picker, and the question is
   * `reachIsMembershipBased` rather than `hasSiteMemberships`.
   *
   * The two differ on **admin**, and the difference is the whole point. An admin
   * does have `site_memberships` rows, so the old question was true for them and
   * they were shown a picker - but their reach is their whole company, bounded
   * by `company_id` and not by those rows. Ticking a site there changed nothing
   * they could see. The section even said so in its own description, which is a
   * form admitting it is decoration.
   *
   * It is deliberately keyed on `member.role` - the **saved** role - not on
   * `form.role`. Demoting somebody to field officer therefore does not make a
   * picker appear mid-edit with nothing sensible in it: the save changes the
   * role, `app.sync_home_site_membership` grants them their default site as it
   * fires, and reopening this dialog shows the picker seeded with that one row
   * for the admin to widen. One decision per save, and the database supplies the
   * floor rather than this form having to guess it.
   */
  const showSites = reachIsMembershipBased(member.role);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (Object.keys(patch).length > 0) {
        await api(`/users/${member.id}`, { method: "PATCH", body: patch });
      }
      if (sitesChanged) {
        // The full intended set, not a diff - a retry after a lost response is
        // then harmless, and the server recomputes what actually changed.
        await api(`/users/${member.id}/sites`, { method: "PUT", body: { site_ids: siteIds } });
      }
      toast.success(`${member.full_name ?? member.email} updated`);
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save those changes"));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Both of these send **real email** and neither can be recalled.
   *
   * They used to fire on the first press. Neither is destructive, so neither is
   * styled as danger - they are asked about because they leave the building:
   * a mis-click puts a second invitation in somebody's inbox, or a
   * password-reset link in the inbox of a colleague who did not ask for one and
   * now has to work out whether they are being phished. They also spend a
   * project-wide send quota that every other flow shares.
   */
  async function resendInvite() {
    const ok = await confirm({
      title: "Resend the invitation?",
      message: `A fresh invitation email goes to ${member.email}. Any link already sent to them keeps working until it expires.`,
      confirmLabel: "Resend it",
      cancelLabel: "Not now",
    });
    if (!ok) return;

    setError(null);
    try {
      await api(`/users/${member.id}/resend-invite`, { method: "POST" });
      setResent(true);
      // The dialog deliberately stays open here, so this toast is raised over an
      // open `<dialog>` - which is why the toaster is a top-layer popover rather
      // than a portalled div. See `Toast.tsx`.
      toast.success("Invitation resent", { description: member.email });
    } catch (err) {
      setError(apiErrorMessage(err, "Could not resend the invitation"));
    }
  }

  /**
   * The admin-initiated half of "Forgot password?" - the same
   * `resetPasswordForEmail` call, sent on somebody else's behalf. The API
   * repeats the rank check this dialog is already gated on, since a role in a
   * client bundle is not a security boundary.
   */
  async function sendReset() {
    const ok = await confirm({
      title: "Send a password reset link?",
      message: `${member.email} gets an email with a link to set a new password. Their current password keeps working until they use it.`,
      confirmLabel: "Send the link",
      cancelLabel: "Not now",
    });
    if (!ok) return;

    setError(null);
    try {
      await api(`/users/${member.id}/reset-password`, { method: "POST" });
      setResetSent(true);
      toast.success("Password reset link sent", { description: member.email });
    } catch (err) {
      setError(apiErrorMessage(err, "Could not send the password reset link"));
    }
  }

  return (
    <Modal
      open
      onClose={() => void requestClose()}
      title={member.full_name ?? member.email}
      className="dialog--wide"
      footer={
        <>
          {/* Support actions on the left, the save on the right. Both are
              things you do *while* looking at somebody, not a reason to have
              opened this panel - so neither one closes it. */}
          {member.status === "invited" && (
            <Button
              variant="ghost"
              size="sm"
              icon={resent ? <MailCheck /> : <Send />}
              disabled={resent}
              onClick={() => void resendInvite()}
              className="dialog__footer-lead"
            >
              {resent ? "Invitation sent" : "Resend invitation"}
            </Button>
          )}
          {canResetPassword && (
            <Button
              variant="ghost"
              size="sm"
              icon={resetSent ? <MailCheck /> : <KeyRound />}
              disabled={resetSent}
              onClick={() => void sendReset()}
              className="dialog__footer-lead"
            >
              {resetSent ? "Reset link sent" : "Reset password"}
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => void requestClose()}>
            Cancel
          </Button>
          <Button form="member-form" type="submit" size="sm" loading={saving} disabled={!dirty}>
            Save changes
          </Button>
        </>
      }
    >
      <Form id="member-form" onSubmit={submit}>
        <FormSection title="Details">
          <div className="form-grid">
            <Field label="Full name" htmlFor="member-name" required>
              <Input
                id="member-name"
                placeholder={PLACEHOLDER.fullName}
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              />
            </Field>

            {/* Read-only. The address is how this identity was created in
                Supabase and changing it here would not change who can sign in
                as them, so it is shown rather than made editable. */}
            <Field label="Email" htmlFor="member-email">
              <Input id="member-email" value={member.email} disabled />
            </Field>

            <Field label="Phone" htmlFor="member-phone">
              <PhoneField
                id="member-phone"
                value={form.phone}
                onChange={(phone) => setForm((f) => ({ ...f, phone }))}
              />
            </Field>

            <Field label="Timezone" htmlFor="member-tz">
              <TimezoneSelect
                id="member-tz"
                value={form.timezone}
                onChange={(timezone) => setForm((f) => ({ ...f, timezone }))}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection title="Access">
          <div className="form-grid">
            <Field
              label="Role"
              htmlFor="member-role"
              // The database permits an admin to demote themselves - clause (b) of
              // `app.enforce_user_write_rules` exempts them and clause (c) only
              // stops them going *above* their rank - and nothing in the API blocks
              // it either. So the guard is here, where somebody would do it by
              // accident while editing their own phone number.
              hint={
                isSelf
                  ? "You cannot change your own role."
                  : "You can only assign roles at or below your own."
              }
            >
              <FilterSelect
                id="member-role"
                variant="field"
                label="Role"
                value={form.role}
                disabled={isSelf}
                options={roleOptions(assignableRoles)}
                onChange={(value) => setForm((f) => ({ ...f, role: value as Role }))}
              />
            </Field>

            <Field
              label="Default location"
              htmlFor="member-site"
              required
              hint="Where sign-in lands them. Setting it also grants them that location."
            >
              <FilterSelect
                id="member-site"
                variant="field"
                label="Default location"
                placeholder="Select a location"
                value={String(form.site_id)}
                options={siteOptions}
                onChange={(value) => setForm((f) => ({ ...f, site_id: value }))}
              />
            </Field>
          </div>
        </FormSection>

        {showSites && (
          <FormSection title="Locations">
            <Field
              label="Assigned locations"
              htmlFor="member-sites"
              hint="Their default location cannot be removed. Change it above first if they are moving crew."
            >
              <MultiSelect
                id="member-sites"
                label="Locations this person can reach"
                placeholder="No locations assigned"
                value={siteIds}
                options={assignableSiteOptions}
                onChange={setSiteIds}
              />
            </Field>
          </FormSection>
        )}

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}

/** The fields that actually changed, in the shape `PATCH /users/:id` accepts. */
function diff(initial: MemberForm, current: MemberForm): Record<string, unknown> {
  const patch: Record<string, unknown> = {};

  if (current.full_name.trim() !== initial.full_name) patch.full_name = current.full_name.trim();
  if (current.role !== initial.role) patch.role = current.role;
  if (current.site_id !== initial.site_id) patch.site_id = current.site_id;
  if (current.timezone !== initial.timezone) patch.timezone = current.timezone;

  // Emptied means cleared, which the schema expresses as null rather than "".
  if (current.phone.trim() !== initial.phone) patch.phone = current.phone.trim() || null;

  return patch;
}

function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/* ─────────────────────────────── invite ─────────────────────────────── */

function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const { user } = useAuth();
  const tenancy = useTenancy();
  const toast = useToast();

  const [values, setValues] = React.useState({ email: "", full_name: "", phone: "" });
  const [role, setRole] = React.useState<Role>("fieldofficer");
  /**
   * The invitee's zone, seeded from the inviter's browser.
   *
   * It used to be sent from `Intl` with no field on screen, which is right about
   * as often as the two people are in the same place - a dispatcher in Hyderabad
   * staffing a Texas yard silently stamped every one of that crew's digests with
   * IST. The invitee can change it on their own profile, but only if they notice,
   * and the person who knows which crew this is is the one filling in this form.
   */
  const [timezone, setTimezone] = React.useState(browserTimezone);
  const [companyId, setCompanyId] = React.useState<string | null>(
    tenancy.companyId ?? tenancy.activeCompanyId
  );
  // Defaults to the site the console is working in, which is almost always the
  // site the person inviting is thinking about.
  const [defaultSiteId, setDefaultSiteId] = React.useState<string | null>(tenancy.activeSiteId);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  // Four typed fields and two pickers. Closing it used to throw all of that away
  // silently, which is the same defect the edit dialogs were fixed for - the
  // guard was on the forms that *change* a record and not on the one that
  // creates it, and an abandoned invitation costs exactly as much retyping.
  const dirty =
    values.email.trim() !== "" || values.full_name.trim() !== "" || values.phone.trim() !== "";
  const requestClose = useDiscardGuard(dirty, submitting, onClose);

  // Only fetched when the caller actually has a choice to make. For everyone
  // else the API pins the company and ignores whatever we send, so a picker
  // would be a control with no effect.
  const { data: companies } = useApi<CompanyListResponse>(
    tenancy.mustChooseCompany && open ? "/companies?pageSize=100" : null
  );
  // Keyed on the chosen company, so switching it refetches rather than
  // re-filtering a list that may never have contained that company's sites. Null
  // until one is chosen, which is only possible for a caller who must choose.
  const { data: companySites } = useApi<SiteListResponse>(
    open && companyId !== null ? `/sites?pageSize=100&company_id=${companyId}` : null
  );

  const sitesInCompany = React.useMemo(() => companySites?.sites ?? [], [companySites]);

  const assignableRoles = ROLES.filter((candidate) => user && canAssignRole(user.role, candidate));

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (defaultSiteId === null) {
      setError("Choose a default location - it is where sign-in lands them.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await api<InviteResponse>("/users/invitations", {
        method: "POST",
        body: {
          email: values.email,
          full_name: values.full_name,
          // Emptied means absent, which the schema expresses as null rather
          // than "" - the same rule the edit dialog's `diff` follows.
          phone: values.phone.trim() || null,
          role,
          // Sent only when it is ours to choose; a bounded caller's is pinned
          // server-side and RLS refuses anything else regardless.
          ...(tenancy.mustChooseCompany ? { company_id: companyId } : {}),
          site_id: defaultSiteId,
          timezone,
        },
      });
      setValues({ email: "", full_name: "", phone: "" });

      /**
       * `emailed: false` means the address already had a GoTrue identity, so
       * `inviteUserByEmail` refused to send and the controller adopted it
       * instead. The person is fully provisioned and **nothing has told them**,
       * which is a materially different outcome from an invitation going out -
       * and the flag was being dropped on the floor here until now, so the
       * console claimed an email had been sent either way.
       */
      if (result.emailed) {
        toast.success("Invitation sent", {
          description: `${values.email} can now set a password and sign in.`,
        });
      } else {
        toast.info(`${values.full_name} was added without an email`, {
          description:
            "That address already had a TaskIQ account, so no invitation was sent. Tell them to sign in - the workspace is waiting for them.",
        });
      }

      onInvited();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not send the invitation"));
    } finally {
      setSubmitting(false);
    }
  }

  // `companySites !== undefined` is "the request came back", not "a company is
  // chosen" - the query is null until one is, so an undefined here also covers
  // the not-yet-asked case and the button stays enabled rather than flickering
  // disabled while the picker is still empty.
  const noSites = companySites !== undefined && sitesInCompany.length === 0 && companyId !== null;

  return (
    <Modal
      open={open}
      onClose={() => void requestClose()}
      title="Invite a teammate"
      className="dialog--wide"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => void requestClose()}>
            Cancel
          </Button>
          <Button
            form="invite-form"
            type="submit"
            size="sm"
            loading={submitting}
            disabled={noSites}
          >
            Send invitation
          </Button>
        </>
      }
    >
      <Form id="invite-form" onSubmit={submit}>
        <p className="prose prose--sm">
          They'll get an email with a link to set their own password.
        </p>

        {/* An invitation needs a home site, so with no site to offer this form
            cannot succeed. Saying so up front beats a validation error after
            four fields have been filled in. */}
        {noSites && (
          <p className="invite-warning">
            This company has no sites yet. Create one first - every person needs a default site.
          </p>
        )}

        <div className="form-grid">
          <Field label="Work email" htmlFor="invite-email" required>
            <Input
              id="invite-email"
              type="email"
              autoComplete="off"
              placeholder={PLACEHOLDER.email}
              value={values.email}
              onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
            />
          </Field>

          <Field label="Full name" htmlFor="invite-name" required>
            <Input
              id="invite-name"
              autoComplete="off"
              placeholder={PLACEHOLDER.fullName}
              value={values.full_name}
              onChange={(e) => setValues((v) => ({ ...v, full_name: e.target.value }))}
            />
          </Field>

          <Field label="Phone" htmlFor="invite-phone">
            <PhoneField
              id="invite-phone"
              value={values.phone}
              onChange={(phone) => setValues((v) => ({ ...v, phone }))}
            />
          </Field>

          <Field label="Timezone" htmlFor="invite-tz" required>
            <TimezoneSelect id="invite-tz" value={timezone} onChange={setTimezone} />
          </Field>

          <Field
            label="Role"
            htmlFor="invite-role"
            required
            // Anything above the inviter's own rank is absent rather than
            // disabled, because Postgres refuses the row outright - offering it
            // greyed out would imply it is a permission somebody could grant.
            hint="You can only assign roles at or below your own."
          >
            <FilterSelect
              id="invite-role"
              variant="field"
              label="Role"
              value={role}
              options={roleOptions(assignableRoles)}
              onChange={(value) => setRole(value as Role)}
            />
          </Field>

          {tenancy.mustChooseCompany && (
            <Field label="Workspace" htmlFor="invite-company" required>
              <FilterSelect
                id="invite-company"
                variant="field"
                label="Workspace"
                placeholder="Select a workspace"
                value={companyId === null ? "" : String(companyId)}
                options={(companies?.companies ?? []).map((company) => ({
                  value: String(company.id),
                  label: company.name,
                }))}
                onChange={(value) => {
                  setCompanyId(value);
                  // The site chosen against the old company cannot survive it.
                  setDefaultSiteId(null);
                }}
              />
            </Field>
          )}

          <Field
            label="Default location"
            htmlFor="invite-site"
            required
            hint="Where sign-in lands them, and the only location granted at first."
          >
            <FilterSelect
              id="invite-site"
              variant="field"
              label="Default location"
              placeholder={companyId === null ? "Choose a workspace first" : "Select a location"}
              value={defaultSiteId === null ? "" : String(defaultSiteId)}
              disabled={companyId === null}
              options={sitesInCompany.map((site) => ({
                value: String(site.id),
                label: site.name,
                hint: site.timezone,
              }))}
              onChange={(id) => {
                setDefaultSiteId(id);
                // Their zone follows the site they are being placed at, which is
                // a better default than the inviter's browser - and it is what
                // the phone has always done. Still a visible, editable field:
                // a default nobody can see is a default nobody can correct.
                const site = sitesInCompany.find((row) => row.id === id);
                if (site) setTimezone(site.timezone);
              }}
            />
          </Field>
        </div>

        {/* No site picker here, deliberately. An invitation grants the default
            site and nothing else - widening reach happens in the edit dialog,
            which goes through PUT /users/:id/sites and the membership policy that
            checks the granting admin may both administer the person and see the
            site. It is also the point at which their real coverage is known. */}

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}

/** The value, but only once it has stopped changing for `delay` milliseconds. */
